import { useEffect } from 'react';

import type { TranslationKey } from '@/i18n/dictionary';
import type { Screen } from '@/lib/kioskSession';
import { SCREEN, useKioskSession } from '@/lib/kioskSession';
import type { Diagnosis } from '@/services/identityLookup';
import { DIAGNOSIS } from '@/services/identityLookup';

import { DifferentEmailDialog } from './DifferentEmailDialog';
import { SeeARepScreen } from './SeeARepScreen';

interface OutcomeCopy {
  /**
   * Whether this outcome continues into the flow, or ends the session.
   *
   * **Decision 0069 settles it for four of the five**: *"After identity confirmation and before the kiosk
   * shows any job orders, it asks 'What brings you in today?'"* — so a diagnosis that resolved leads to the
   * arrival step, and the assistance outcome is the one that does not, because there is nothing for the
   * person to choose from a machine.
   */
  readonly continues: boolean;
  /**
   * Where a continuing outcome goes.
   *
   * **The branch is the identity result, not what the candidate said** [Operator, 2026-08-31]. Somebody can
   * tap "I've never applied" and still have a record — and then the right screen is the one that shows them
   * what is on file, not a blank form asking for it again.
   */
  readonly destination?: Screen;
  readonly headingKey: TranslationKey;
  readonly bodyKey: TranslationKey;
  readonly actionKey: TranslationKey;
}

/**
 * One entry per diagnosis the api can return.
 *
 * `assistance` is deliberately indistinguishable to the candidate from every cause behind it — an ambiguous
 * match, a barred record, a lookup that timed out. The api sends no reason, and this table gives it nowhere
 * to render one even if a later change started sending it.
 */
const COPY: Record<Exclude<Diagnosis, 'different-email'>, OutcomeCopy> = {
  [DIAGNOSIS.new]: {
    continues: true,
    // Not found in Bullhorn, so there is nothing to confirm — the profile is created here.
    destination: SCREEN.application,
    headingKey: 'routing.new.heading',
    bodyKey: 'routing.new.body',
    actionKey: 'routing.continue',
  },
  [DIAGNOSIS.returning]: {
    continues: true,
    // Found in Bullhorn, so there is something to confirm before the hub [Operator, 2026-08-31].
    destination: SCREEN.profileUpdate,
    headingKey: 'routing.returning.heading',
    bodyKey: 'routing.returning.body',
    actionKey: 'routing.continue',
  },
  [DIAGNOSIS.resume]: {
    continues: true,
    // Found in Bullhorn, so there is something to confirm before the hub [Operator, 2026-08-31].
    destination: SCREEN.profileUpdate,
    headingKey: 'routing.resume.heading',
    bodyKey: 'routing.resume.body',
    actionKey: 'routing.continue',
  },
  [DIAGNOSIS.activePlacement]: {
    continues: true,
    // Found in Bullhorn, so there is something to confirm before the hub [Operator, 2026-08-31].
    destination: SCREEN.profileUpdate,
    headingKey: 'routing.activePlacement.heading',
    bodyKey: 'routing.activePlacement.body',
    actionKey: 'routing.continue',
  },
  [DIAGNOSIS.assistance]: {
    continues: false,
    headingKey: 'routing.assistance.heading',
    bodyKey: 'routing.assistance.body',
    actionKey: 'routing.assistance.startOver',
  },
};

/** Where a continuing outcome leads — asked by the effect below and by the different-email prompt alike. */
function destinationOf(copy: OutcomeCopy): Screen {
  return copy.destination ?? SCREEN.arrivalReason;
}

/**
 * Where the candidate is told which flow they are in.
 *
 * ⚠️ **This screen has no frame of its own, and that is a real gap rather than an oversight.** Reading the
 * Figma page showed that "03 · Routing" is the LOADING state (see `CheckingScreen`), and that each outcome
 * continues into a screen owned by a different story — *03 · Application (profile)* (KAP), *03 · Returning
 * profile* and *04 · Active associate* (the returning capability), and *See a rep (escalation)* (KVE-1).
 * None of those is built yet.
 *
 * So this is a **deliberate placeholder**: it uses the design's own card, type scale and button so it does
 * not look foreign, and it names the outcome plus one way forward — because a diagnosis that led nowhere
 * would leave a person facing a blank screen. It should be deleted as each destination story lands, not
 * decorated.
 *
 * **Where "one way forward" leads is not a placeholder decision.** [Decision 0069] is explicit: *"After
 * identity confirmation and before the kiosk shows any job orders, it asks 'What brings you in today?'"* —
 * so a resolved diagnosis continues into the arrival step, whichever of the four it was. Until this was
 * wired the button said *Continue* and ended the session, which meant no candidate could reach any screen
 * past the diagnosis by using the kiosk, and a label that did the opposite of what it said.
 *
 * The assistance outcome is the one that does NOT continue, and that is not an omission: there is nothing
 * for somebody to choose from a machine once the answer is "see a representative".
 */
export function RoutingScreen() {
  const result = useKioskSession((state) => state.lookupResult);
  const goTo = useKioskSession((state) => state.goTo);

  // A missing result can only mean the store was cleared under this screen (idle timeout, Back). The neutral
  // hand-off is the right resting place: it is the outcome that assumes least about who is standing there.
  const diagnosis = result?.diagnosis ?? DIAGNOSIS.assistance;
  const copy = diagnosis === DIAGNOSIS.differentEmail ? undefined : COPY[diagnosis];

  /*
   * An outcome that CONTINUES does not stop to say so [Operator, 2026-09-02].
   *
   * This used to render "Welcome back — continue" and wait for a tap. Greeting somebody to tell them a
   * lookup succeeded, on the way to the screen that greets them again, is a tap that carries no decision:
   * the candidate cannot choose anything here, and both destinations open with their own heading.
   *
   * It also has no frame in Figma — "03 · Routing" is the LOADING state, and no outcome screen was ever
   * drawn — so this was Rootstrap's invention rather than a design the flow asked for.
   *
   * `assistance` still renders, and that is the whole point of keeping the screen: it is the one outcome
   * with nothing to continue to, and the person needs to be told.
   */
  useEffect(() => {
    if (copy?.continues === true) goTo(destinationOf(copy));
  }, [copy, goTo]);

  /*
   * The different-email case is a confirmation, not an outcome: it asks before anything is decided. Once
   * answered, the candidate is a returning match [0030], so the prompt continues to where one goes.
   */
  if (result?.diagnosis === DIAGNOSIS.differentEmail) {
    return (
      <DifferentEmailDialog
        continueTo={destinationOf(COPY[DIAGNOSIS.returning])}
        emailOnFile={result.emailOnFile}
      />
    );
  }

  // Already on its way — rendering the greeting for one frame would flash it.
  if (copy === undefined || copy.continues) return null;

  /*
   * The hand-off has its own screen now — it is the only outcome that stops here, and the frame gives it a
   * Back control, a Rep chip and a callout naming what the candidate came for. What was left in this file
   * was four words and a control that wiped the session.
   */
  return <SeeARepScreen />;
}
